import { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { KeyRound, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const PIN_LENGTH = 4;

/**
 * Generates a unique 4-digit PIN that doesn't collide with any existing PINs.
 * @param {string[]} existingPins - Array of PINs already in use within the company
 * @returns {string} A unique 4-digit PIN
 */
function generateUniquePin(existingPins = []) {
  const pinSet = new Set(existingPins);
  const maxAttempts = 100;

  for (let i = 0; i < maxAttempts; i++) {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    if (!pinSet.has(pin)) return pin;
  }

  // Fallback: find any unused 4-digit number
  for (let n = 1000; n <= 9999; n++) {
    const pin = String(n);
    if (!pinSet.has(pin)) return pin;
  }

  throw new Error('No available PINs remaining (all 9000 used)');
}

/**
 * Fetches all kiosk PINs currently in use for a company.
 */
async function fetchCompanyPins(companyId) {
  const { data, error } = await supabase
    .from('company_members')
    .select('user_profiles:user_id ( kiosk_pin )')
    .eq('company_id', companyId);

  if (error) {
    console.error('Failed to fetch company PINs:', error);
    return [];
  }

  return (data || [])
    .map((m) => m.user_profiles?.kiosk_pin)
    .filter(Boolean);
}

/**
 * KioskPinManager - manages a user's auto-generated 4-digit kiosk PIN.
 *
 * PINs are always 4 digits and auto-generated to be unique within the company.
 * Users cannot manually set or edit PINs — only generate or regenerate.
 *
 * Supports two layout modes:
 *   - Standard (compact=false): full card with header and description
 *   - Compact (compact=true): single-row layout for use in edit forms
 *
 * @param {string} userId - UUID of the user profile
 * @param {string|null} currentPin - the user's current PIN (if any)
 * @param {string} companyId - company UUID (for uniqueness check)
 * @param {function} onPinChange - callback when PIN changes, receives new PIN string
 * @param {boolean} isAdmin - if true, always show PIN in plain text
 * @param {boolean} compact - if true, render as a single row
 */
export function KioskPinManager({
  userId,
  currentPin = null,
  companyId,
  onPinChange,
  isAdmin = false,
  compact = false,
}) {
  const [generating, setGenerating] = useState(false);
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);

  // -------------------------------------------------------------------
  // Generate (or regenerate) a unique PIN and save it
  // -------------------------------------------------------------------
  const generateAndSave = useCallback(async () => {
    if (!companyId || !userId) return;

    setGenerating(true);
    try {
      const existingPins = await fetchCompanyPins(companyId);
      const newPin = generateUniquePin(existingPins);

      const { error: dbError } = await supabase
        .from('user_profiles')
        .update({ kiosk_pin: newPin })
        .eq('id', userId);

      if (dbError) {
        if (
          dbError.code === '23505' ||
          dbError.message?.toLowerCase().includes('unique') ||
          dbError.message?.toLowerCase().includes('duplicate')
        ) {
          toast.error('PIN collision — please try again');
          return;
        }
        throw dbError;
      }

      toast.success(currentPin ? 'PIN regenerated' : 'PIN generated');
      setConfirmRegenOpen(false);
      onPinChange?.(newPin);
    } catch (err) {
      console.error('Failed to generate kiosk PIN:', err);
      toast.error('Failed to generate PIN');
    } finally {
      setGenerating(false);
    }
  }, [companyId, userId, currentPin, onPinChange]);

  // -------------------------------------------------------------------
  // Handle generate button click — confirm if replacing existing PIN
  // -------------------------------------------------------------------
  const handleGenerateClick = () => {
    if (currentPin) {
      setConfirmRegenOpen(true);
    } else {
      generateAndSave();
    }
  };

  // -------------------------------------------------------------------
  // PIN display
  // -------------------------------------------------------------------
  const pinDisplay = currentPin ? (
    <span className="font-mono text-sm font-medium tracking-widest">
      {currentPin}
    </span>
  ) : (
    <span className="text-sm text-muted-foreground">No PIN assigned</span>
  );

  // -------------------------------------------------------------------
  // Compact layout
  // -------------------------------------------------------------------
  if (compact) {
    return (
      <>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <KeyRound className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Label className="text-sm font-medium whitespace-nowrap">Kiosk PIN</Label>
            {pinDisplay}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateClick}
            disabled={generating}
          >
            {generating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {currentPin ? 'Regenerate' : 'Generate PIN'}
          </Button>
        </div>

        <RegenConfirmDialog
          open={confirmRegenOpen}
          onOpenChange={setConfirmRegenOpen}
          generating={generating}
          onConfirm={generateAndSave}
        />
      </>
    );
  }

  // -------------------------------------------------------------------
  // Standard card layout
  // -------------------------------------------------------------------
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Kiosk PIN</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A {PIN_LENGTH}-digit PIN used to identify this user on shared kiosk devices for
            check-in. PINs are auto-generated and unique within your company.
          </p>

          {currentPin ? (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="font-mono text-lg font-semibold tracking-widest">
                {currentPin}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateClick}
                disabled={generating}
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Regenerate
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-6">
              <p className="text-sm text-muted-foreground mb-3">No PIN assigned</p>
              <Button
                variant="outline"
                onClick={handleGenerateClick}
                disabled={generating}
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                Generate PIN
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <RegenConfirmDialog
        open={confirmRegenOpen}
        onOpenChange={setConfirmRegenOpen}
        generating={generating}
        onConfirm={generateAndSave}
      />
    </>
  );
}

/**
 * Confirm dialog shown when regenerating an existing PIN.
 */
function RegenConfirmDialog({ open, onOpenChange, generating, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Regenerate Kiosk PIN?</DialogTitle>
          <DialogDescription>
            This will replace the current PIN with a new randomly generated one.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          The employee will need to use the new PIN for kiosk check-in.
        </p>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={generating}
          >
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={generating}>
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default KioskPinManager;
